import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GoogleMap, Marker, OverlayViewF, OverlayView, InfoWindowF, useJsApiLoader } from "@react-google-maps/api";
import qrImage from "../assets/google-reviews-1-.png";
import UserService from "../services/user.service";
import HomeQrStudio from "./HomeQrStudio";
import MapShootingStars from "./MapShootingStars";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const MAP_LIBRARIES = ["places"];
const DEFAULT_LOCATION = { lat: 50.9375, lng: 6.9603 }; // Köln
const MAP_ENTRIES_REFRESH_MS = 60000;
const MAP_FIT_MAX_ZOOM = 14;
const STAR_HIGHLIGHT_MS = 2600;
const markerKey = (lat, lng) => `${Number(lat).toFixed(3)}|${Number(lng).toFixed(3)}`;
const starOffset = (width, height) => ({ x: -(width / 2), y: -(height / 2) });

const Home = () => {
  const navigate = useNavigate();
  const { isLoaded } = useJsApiLoader({
    id: "myn-google-maps",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const [currentPosition, setCurrentPosition] = useState(DEFAULT_LOCATION);
  const [geoError, setGeoError] = useState(null);

  // Kundenstandorte, die einen QR-Code gebucht und aktiv gestellt haben. Sie
  // werden als Sterne auf der Karte angezeigt und wandern beim Zoomen mit.
  const [activityMarkers, setActivityMarkers] = useState([]);

  // Live-Karte: Karteninstanz, gewaehltes Unternehmen, Sternschnuppen.
  const [mapInstance, setMapInstance] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [flights, setFlights] = useState([]);
  const [highlighted, setHighlighted] = useState({});
  const [lastFlightTitle, setLastFlightTitle] = useState("");
  const [geoSettled, setGeoSettled] = useState(false);
  const [mapStartCenter] = useState(DEFAULT_LOCATION);
  const userMovedMapRef = useRef(false);
  const flightCursorRef = useRef(null);

  // Selbstbucher-Angebot. Solange `enabled` false ist (Feature abgeschaltet,
  // kein Eigentümer-Haupt-VO hinterlegt oder kein digital buchbarer Dienst),
  // bleibt die zugehörige Sektion vollständig ausgeblendet.
  const [selfServiceOffer, setSelfServiceOffer] = useState({ enabled: false, host: null, services: [] });

  useEffect(() => {
    let active = true;

    UserService.getSelfServiceOffer()
      .then((response) => {
        if (active && response && response.data) setSelfServiceOffer(response.data);
      })
      .catch(() => {
        // Die Startseite bleibt ohne Angebot bedienbar; kein Fehler für Besucher.
        if (active) setSelfServiceOffer({ enabled: false, host: null, services: [] });
      });

    return () => { active = false; };
  }, []);

  // Sterne der Karte laden und regelmaessig aktualisieren, damit neu gebuchte
  // Unternehmen ohne Neuladen der Seite erscheinen. Bei Netzfehlern bleiben
  // die zuletzt geladenen Sterne stehen.
  const mapEntriesActiveRef = useRef(true);
  const activityMarkersRef = useRef(activityMarkers);
  activityMarkersRef.current = activityMarkers;
  const loadMapEntries = useCallback(() => {
    UserService.getSelfServiceMapEntries()
      .then((response) => {
        const entries = response && response.data && Array.isArray(response.data.entries)
          ? response.data.entries
          : [];
        if (mapEntriesActiveRef.current) setActivityMarkers(entries);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    mapEntriesActiveRef.current = true;
    loadMapEntries();
    const timer = window.setInterval(loadMapEntries, MAP_ENTRIES_REFRESH_MS);
    return () => {
      mapEntriesActiveRef.current = false;
      window.clearInterval(timer);
    };
  }, [loadMapEntries]);

  // Kartenausschnitt: alle Sterne und der eigene Standort sind sichtbar, bis
  // der Besucher die Karte selbst verschiebt oder zoomt.
  useEffect(() => {
    if (!mapInstance || !window.google || userMovedMapRef.current) return undefined;
    const { maps } = window.google;
    if (activityMarkers.length === 0) {
      mapInstance.setCenter(currentPosition);
      mapInstance.setZoom(13);
      return undefined;
    }
    const bounds = new maps.LatLngBounds();
    activityMarkers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
    if (geoSettled) bounds.extend(currentPosition);
    mapInstance.fitBounds(bounds, 56);
    const idle = maps.event.addListenerOnce(mapInstance, "idle", () => {
      if (mapInstance.getZoom() > MAP_FIT_MAX_ZOOM) mapInstance.setZoom(MAP_FIT_MAX_ZOOM);
    });
    return () => maps.event.removeListener(idle);
  }, [mapInstance, activityMarkers, currentPosition, geoSettled]);

  // Nur echte Eingaben (Ziehen, Mausrad, Tippen, Zoom-Tasten) stoppen den
  // automatischen Kartenausschnitt.
  const markUserMove = () => {
    userMovedMapRef.current = true;
  };

  // Sternschnuppen: neue Weiterleitungen in kurzen Abstaenden abfragen.
  // Pausiert, solange der Tab im Hintergrund ist.
  useEffect(() => {
    if (!isLoaded) return undefined;
    let stopped = false;
    let timer = null;
    let delay = 5000;

    const schedule = () => {
      if (!stopped) timer = window.setTimeout(poll, delay);
    };
    const poll = () => {
      if (stopped) return;
      if (document.hidden) {
        schedule();
        return;
      }
      UserService.getSelfServiceMapFlights(flightCursorRef.current)
        .then((response) => {
          const data = (response && response.data) || {};
          if (data.enabled === false) {
            stopped = true;
            return;
          }
          flightCursorRef.current = Number(data.cursor) || 0;
          if (Number(data.poll_ms) > 0) delay = Number(data.poll_ms);
          const incoming = Array.isArray(data.flights) ? data.flights : [];
          if (incoming.length > 0) {
            setFlights((prev) => {
              const known = new Set(prev.map((f) => f.id));
              return prev.concat(incoming.filter((f) => !known.has(f.id)));
            });
          }
        })
        .catch(() => {})
        .finally(schedule);
    };

    poll();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [isLoaded]);

  const handleFlightArrive = useCallback((flight) => {
    const key = markerKey(flight.to.lat, flight.to.lng);
    setLastFlightTitle(flight.title || "");
    setHighlighted((prev) => ({ ...prev, [key]: Date.now() }));
    window.setTimeout(() => {
      setHighlighted((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, STAR_HIGHLIGHT_MS);
    if (!activityMarkersRef.current.some((m) => markerKey(m.lat, m.lng) === key)) loadMapEntries();
  }, [loadMapEntries]);

  const handleFlightDone = useCallback((id) => {
    setFlights((prev) => prev.filter((f) => f.id !== id));
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentPosition({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setGeoSettled(true);
        },
        (error) => {
          setGeoSettled(true);
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setGeoError("Standortfreigabe wurde abgelehnt – es wird Köln als Standort angezeigt.");
              break;
            case error.POSITION_UNAVAILABLE:
              setGeoError("Standortinformationen sind nicht verfügbar.");
              break;
            case error.TIMEOUT:
              setGeoError("Die Standortabfrage hat zu lange gedauert.");
              break;
            default:
              setGeoError("Der Standort konnte nicht ermittelt werden.");
              break;
          }
        }
      );
    } else {
      setGeoSettled(true);
      setGeoError("Geolocation wird von diesem Browser nicht unterstützt.");
    }
  }, []);

  const goToRegister = () => navigate("/register");

  const mapContainerStyle = { width: "100%", height: "100%" };

  const products = [
    {
      icon: "⭐",
      badge: "Bewertungen",
      title: "MYN Reviews",
      tag: "Mehr 5-Sterne-Google-Bewertungen",
      text: "Per QR-Code oder NFC gelangen Ihre Kunden mit einem Klick zur Google-Bewertung – ganz ohne App. Mehr Rezensionen, bessere Sichtbarkeit, mehr Vertrauen.",
      ctaLabel: "Jetzt starten",
      to: "/register",
      external: false,
      accent: "",
    },
    {
      icon: "📞",
      badge: "Telefonie & Privatsphäre",
      title: "WIRRT",
      tag: "Deine Schutznummer per QR",
      text: "Verkaufe öffentlich, bleib privat: eine seriöse deutsche Zweitnummer für Inserate, Websites und öffentliche Kontakte – in Minuten per QR-Code aktiviert.",
      ctaLabel: "Zu wirrt.com",
      to: "https://www.wirrt.com",
      external: true,
      accent: "",
    },
    {
      icon: "🚀",
      badge: "In Vorbereitung",
      title: "Weitere Produkte",
      tag: "Die Plattform wächst",
      text: "Laufend kommen neue digitale Produkte hinzu. Als Partner integrieren und vertreiben Sie diese unter Ihren eigenen Konditionen.",
      ctaLabel: "Partner werden",
      to: "/register",
      external: false,
      accent: "myn-product--soon",
    },
  ];

  const steps = [
    {
      title: "Produkt wählen oder erstellen",
      text: "Starten Sie mit einem fertigen Produkt aus dem Schaufenster oder bilden Sie Ihr eigenes digitales Angebot auf der Plattform ab.",
    },
    {
      title: "Über das Netzwerk vertreiben",
      text: "Verteilen Sie Produkte über ein mehrstufiges Vertriebsnetz – von der Hauptvertriebsebene über Reseller bis zum Vertriebspartner.",
    },
    {
      title: "Verwalten & abrechnen",
      text: "Tarife, Rechnungen, Belegformate und Statistiken laufen zentral und automatisiert zusammen – inklusive Zahlungsabwicklung.",
    },
  ];

  const features = [
    { icon: "🧩", title: "Multi-Produkt-Plattform", text: "Verwalten Sie beliebig viele digitale Produkte zentral an einem Ort – ein System statt vieler Insellösungen." },
    { icon: "🌐", title: "Mehrstufiger Vertrieb", text: "Hauptvertrieb, Reseller und Vertriebspartner sind sauber abgebildet und beliebig skalierbar." },
    { icon: "🧾", title: "Automatische Abrechnung", text: "Tarife, Rechnungen und individuelle Belegnummernformate – vollständig integriert inkl. Zahlungsabwicklung." },
    { icon: "🏷️", title: "White-Label & Branding", text: "Produkte, Vorlagen und Belege erscheinen im eigenen Auftritt Ihrer Partner und Reseller." },
    { icon: "📊", title: "Statistiken & Tracking", text: "Klicks, Aktivierungen und Umsätze sind jederzeit transparent nachvollziehbar." },
    { icon: "🔒", title: "DSGVO-konform", text: "Datenschutzfreundlich umgesetzt – nach europäischen Sicherheits- und Datenschutzstandards." },
  ];

  const audiences = [
    { title: "Hauptvertrieb (MainVO)", text: "Steuern Sie Produkte, Reseller, Chargen-Generierung und Abrechnung zentral über ein leistungsfähiges Dashboard." },
    { title: "Reseller (SubVO)", text: "Eigene Tarife, Zahlungsarten und Vereinbarungen – vertreiben Sie die Produkte unter Ihren eigenen Konditionen." },
    { title: "Vertriebspartner (ADM)", text: "Betreuen Sie eigene Kunden, verwalten Sie Chargen und behalten Sie Ihre Provisionen jederzeit im Blick." },
    { title: "Endkunden", text: "Profitieren Sie direkt von den Produkten – einfach, schnell und ohne technische Hürden." },
  ];

  const faqs = [
    { q: "Was ist MYN.CLUB genau?", a: "Eine Plattform zum Erstellen, Verwalten und Vertreiben digitaler Produkte über ein mehrstufiges Vertriebs- und Distributionsnetz – ein System für viele Produkte." },
    { q: "Welche Produkte gibt es?", a: "Aktuell unter anderem MYN Reviews (mehr Google-Bewertungen per QR/NFC) und WIRRT (QR-Schutznummer). Weitere Produkte folgen laufend." },
    { q: "Kann ich als Partner eigene Produkte vertreiben?", a: "Ja. Über das mehrstufige Reseller-Modell (Hauptvertrieb, Reseller, Vertriebspartner) vertreiben Sie Produkte zu eigenen Konditionen." },
    { q: "Wie funktioniert die Abrechnung?", a: "Über integrierte Tarife, Rechnungen, Belegformate und Zahlungsarten – inklusive Online-Zahlungsabwicklung." },
    { q: "Ist die Plattform DSGVO-konform?", a: "Ja. Wir erheben nur notwendige Daten und setzen Datenschutz nach europäischen Standards um." },
  ];

  return (
    <div className="myn-page">
      {/* ===== SELBST BUCHEN / QR-STUDIO (OBERSTE SICHTBARE STELLE) =====
          Sichtbar, sobald mindestens ein Dienst eine nicht-physikalische
          Transportart besitzt, ein Eigentümer-Haupt-VO hinterlegt und der
          Feature-Schalter aktiv ist. Prominent ganz oben für direkte Suche & Test. */}
      {selfServiceOffer.enabled && (
        <HomeQrStudio
          isLoaded={isLoaded}
          hostName={selfServiceOffer.host && selfServiceOffer.host.company_name}
        />
      )}

      {/* ===== HERO ===== */}
      <section className="myn-section myn-hero">
        <div className="myn-container myn-hero__grid">
          <div>
            <span className="myn-eyebrow">Plattform für digitale Produkte &amp; Vertrieb</span>
            <h1 className="myn-h1">
              Digitale Produkte erstellen, verwalten und{" "}
              <span className="myn-gradient-text">im Netzwerk vertreiben</span>
            </h1>
            <p className="myn-lead">
              MYN.CLUB ist die zentrale Plattform, auf der Sie digitale Produkte
              erstellen, verwalten und über ein mehrstufiges Vertriebs- und
              Distributionsnetz skalieren – von Google-Bewertungen bis zur
              QR-Schutznummer. Ein System, viele Produkte, unbegrenzte Reichweite.
            </p>
            <div className="myn-hero__actions">
              <button onClick={goToRegister} className="myn-btn myn-btn--primary">
                Jetzt kostenlos starten
              </button>
              <a href="#produkte" className="myn-btn myn-btn--ghost">
                Produkte ansehen
              </a>
            </div>
            <div className="myn-stars" aria-label="Mehrere Produkte auf einer Plattform">★★★★★
              <span className="myn-text-muted" style={{ fontSize: "0.95rem", marginLeft: "0.5rem", letterSpacing: 0 }}>
                Eine Plattform – viele starke Produkte
              </span>
            </div>
          </div>

          <div className="myn-hero__media">
            <div className="myn-card myn-card--hover">
              <img src={qrImage} alt="QR-Code-basierte Produkte auf der MYN.CLUB Plattform" />
              <p className="myn-h3" style={{ marginTop: "1rem" }}>Ein Code – viele Möglichkeiten</p>
              <p className="myn-text-muted" style={{ margin: 0 }}>
                QR &amp; NFC als Basis für jedes Produkt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATISTIK-BAND (Folgeprompt 16, 3.4) ===== */}
      {/* Variante (a): dauerhaft kompaktes Band, je Punkt ein kleines Icon links.
          Nimmt deutlich weniger Platz ein als zuvor. */}
      <section className="myn-section myn-section--alt" style={{ paddingBlock: "clamp(0.75rem,2vw,1.25rem)" }}>
        <div className="myn-container">
          <ul className="myn-card myn-stats" aria-label="Plattform-Eckdaten">
            {[
              { icon: "🧩", value: "Multi", label: "Mehrere Produkte" },
              { icon: "🌐", value: "3+", label: "Vertriebsebenen" },
              { icon: "📱", value: "QR + NFC", label: "Technologie-Basis" },
              { icon: "🔒", value: "DSGVO", label: "Datenschutz-konform" },
            ].map((s, i) => (
              <li className="myn-stat" key={i}>
                <span className="myn-stat__icon" aria-hidden="true">{s.icon}</span>
                <span className="myn-stat__text">
                  <span className="myn-stat__value">{s.value}</span>
                  <span className="myn-stat__label">{s.label}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== PRODUKT-SCHAUFENSTER ===== */}
      <section id="produkte" className="myn-section">
        <div className="myn-container">
          <div className="myn-text-center" style={{ marginBottom: "2.5rem" }}>
            <span className="myn-eyebrow">Das Schaufenster</span>
            <h2 className="myn-h2">Unsere Produkte</h2>
            <p className="myn-lead myn-measure myn-mx-auto">
              Jedes Produkt ist für sich stark – gemeinsam laufen sie auf einer
              Plattform. Erstellen, verwalten und vertreiben Sie sie über ein
              einziges System.
            </p>
          </div>
          <div className="myn-grid">
            {products.map((p, i) => (
              <div className={`myn-card myn-card--hover myn-product ${p.accent}`} key={i}>
                <div className="myn-product__icon" aria-hidden="true">{p.icon}</div>
                <span className="myn-product__badge">{p.badge}</span>
                <div className="myn-product__body">
                  <h3 className="myn-h3" style={{ marginBottom: "0.25rem" }}>{p.title}</h3>
                  <p className="myn-product__tag">{p.tag}</p>
                  <p className="myn-text-muted" style={{ margin: 0 }}>{p.text}</p>
                </div>
                {p.external ? (
                  <a className="myn-btn myn-btn--primary myn-product__cta" href={p.to} target="_blank" rel="noreferrer">
                    {p.ctaLabel}
                  </a>
                ) : (
                  <Link className="myn-btn myn-btn--primary myn-product__cta" to={p.to}>
                    {p.ctaLabel}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SO FUNKTIONIERT'S ===== */}
      <section id="so-funktionierts" className="myn-section myn-section--alt">
        <div className="myn-container">
          <div className="myn-text-center" style={{ marginBottom: "2.5rem" }}>
            <span className="myn-eyebrow">In 3 Schritten</span>
            <h2 className="myn-h2">So funktioniert die Plattform</h2>
            <p className="myn-lead myn-measure myn-mx-auto">
              Vom Produkt bis zur Auszahlung – ein durchgängiger, automatisierter Ablauf.
            </p>
          </div>
          <div className="myn-grid">
            {steps.map((step, i) => (
              <div className="myn-card myn-card--hover" key={i}>
                <div className="myn-step__num">{i + 1}</div>
                <h3 className="myn-h3">{step.title}</h3>
                <p className="myn-text-muted" style={{ margin: 0 }}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VORTEILE ===== */}
      <section className="myn-section">
        <div className="myn-container">
          <div className="myn-text-center" style={{ marginBottom: "2.5rem" }}>
            <span className="myn-eyebrow">Ihre Vorteile</span>
            <h2 className="myn-h2">Warum MYN.CLUB als Plattform</h2>
          </div>
          <div className="myn-grid">
            {features.map((f, i) => (
              <div className="myn-card myn-card--hover" key={i}>
                <div className="myn-feature__icon" aria-hidden="true">{f.icon}</div>
                <h3 className="myn-h3">{f.title}</h3>
                <p className="myn-text-muted" style={{ margin: 0 }}>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ZIELGRUPPEN / VERTRIEBSMODELL ===== */}
      <section className="myn-section myn-section--alt">
        <div className="myn-container">
          <div className="myn-text-center" style={{ marginBottom: "2.5rem" }}>
            <span className="myn-eyebrow">Für wen</span>
            <h2 className="myn-h2">Ein Modell – vom Hauptvertrieb bis zum Endkunden</h2>
            <p className="myn-lead myn-measure myn-mx-auto">
              MYN.CLUB bildet ein mehrstufiges Vertriebs- und Distributionsmodell
              vollständig ab – für jedes Produkt, transparent und skalierbar.
            </p>
          </div>
          <div className="myn-grid">
            {audiences.map((a, i) => (
              <div className="myn-card" key={i}>
                <h3 className="myn-h3">{a.title}</h3>
                <p className="myn-text-muted" style={{ margin: 0 }}>{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LIVE-KARTE ===== */}
      <section className="myn-section">
        <div className="myn-container">
          <div className="myn-text-center" style={{ marginBottom: "1.75rem" }}>
            <span className="myn-eyebrow">Live-Karte</span>
            <h2 className="myn-h2">Aktivität in Ihrer Region</h2>
            <p className="myn-lead myn-measure myn-mx-auto">
              {activityMarkers.length > 0
                ? `Jedes Unternehmen, das einen QR-Code gebucht und aktiv gestellt hat, erscheint hier als Stern. Aktuell sind es ${activityMarkers.length}. Tippen Sie auf einen Stern, um das Unternehmen zu sehen.`
                : "Sobald Unternehmen einen QR-Code gebucht und aktiv gestellt haben, erscheinen sie hier als Sterne auf der Karte."}
            </p>
          </div>
          <div className="myn-card" style={{ padding: "0.75rem" }}>
            <div className="myn-map-wrap" onWheel={markUserMove} onPointerDown={markUserMove} onKeyDown={markUserMove}>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapStartCenter}
                  zoom={13}
                  onLoad={setMapInstance}
                  onUnmount={() => setMapInstance(null)}
                  onDragStart={markUserMove}
                  onClick={() => setSelectedMarker(null)}
                  options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    fullscreenControl: true,
                    clickableIcons: false,
                    gestureHandling: "cooperative",
                  }}
                >
                  <Marker position={currentPosition} />
                  {activityMarkers.map((m) => {
                    const key = markerKey(m.lat, m.lng);
                    return (
                      <OverlayViewF
                        key={key}
                        position={{ lat: m.lat, lng: m.lng }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={starOffset}
                      >
                        <button
                          type="button"
                          className={`myn-map-star${highlighted[key] ? " is-hit" : ""}${selectedMarker && markerKey(selectedMarker.lat, selectedMarker.lng) === key ? " is-selected" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMarker(m);
                          }}
                          title={m.title}
                          aria-label={`${m.title} auf der Karte anzeigen`}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M12 2l2.9 6 6.6.9-4.8 4.6 1.2 6.6L12 18.6 6.1 20.7l1.2-6.6L2.5 8.9 9.1 8z" />
                          </svg>
                        </button>
                      </OverlayViewF>
                    );
                  })}
                  {selectedMarker && (
                    <InfoWindowF
                      position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                      onCloseClick={() => setSelectedMarker(null)}
                      options={{ pixelOffset: new window.google.maps.Size(0, -22), maxWidth: 280 }}
                    >
                      <div className="myn-map-info">
                        <strong className="myn-map-info__title">{selectedMarker.title}</strong>
                        {selectedMarker.address && (
                          <span className="myn-map-info__address">{selectedMarker.address}</span>
                        )}
                        {selectedMarker.code && (
                          <span className="myn-map-info__actions">
                            {selectedMarker.redeem_url && (
                              <a href={selectedMarker.redeem_url} target="_blank" rel="noopener noreferrer">
                                Jetzt bewerten
                              </a>
                            )}
                            <a href={`/v/${encodeURIComponent(selectedMarker.code)}`} target="_blank" rel="noopener noreferrer">
                              Visitenkarte
                            </a>
                          </span>
                        )}
                      </div>
                    </InfoWindowF>
                  )}
                  <MapShootingStars
                    map={mapInstance}
                    flights={flights}
                    onArrive={handleFlightArrive}
                    onDone={handleFlightDone}
                  />
                </GoogleMap>
              ) : (
                <p className="myn-text-muted">Karte wird geladen …</p>
              )}
            </div>
            <p className="myn-map-live" aria-live="polite">
              <span className="myn-map-live__dot" aria-hidden="true" />
              {lastFlightTitle
                ? `Gerade eben: Weiterleitung zu ${lastFlightTitle}`
                : "Live: Jede Weiterleitung zu einem Unternehmen fliegt als Sternschnuppe über die Karte."}
            </p>
            {geoError && (
              <p className="myn-text-muted" style={{ margin: "0.75rem 0 0", fontSize: "0.9rem" }}>
                {geoError}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="myn-section myn-section--alt">
        <div className="myn-container" style={{ maxWidth: "820px" }}>
          <div className="myn-text-center" style={{ marginBottom: "2rem" }}>
            <span className="myn-eyebrow">FAQ</span>
            <h2 className="myn-h2">Häufige Fragen</h2>
          </div>
          <div className="myn-faq">
            {faqs.map((item, i) => (
              <details key={i}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="myn-section">
        <div className="myn-container">
          <div className="myn-cta-band">
            <h2 className="myn-h2" style={{ color: "#fff" }}>
              Bereit, Produkte zu erstellen und zu vertreiben?
            </h2>
            <p className="myn-lead" style={{ color: "rgba(255,255,255,0.92)" }}>
              Starten Sie kostenlos auf der Plattform und skalieren Sie Ihr Geschäft über das Vertriebsnetz.
            </p>
            <div className="myn-hero__actions" style={{ justifyContent: "center", marginBottom: 0 }}>
              <button onClick={goToRegister} className="myn-btn myn-btn--ghost">
                Jetzt kostenlos starten
              </button>
              <Link to="/login" className="myn-btn myn-btn--ghost" style={{ background: "transparent", color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}>
                Anmelden
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
